/* eslint-disable no-console */
/**
 * @author Harsh Raj <harsh.raj@314ecorp.com>
 * @description Service worker registration for web
 */

import _ from 'lodash';

// This lets the app load faster on subsequent visits in production, and gives
// it offline capabilities. However, it also means that developers (and users)
// will only see deployed updates on subsequent visits to a page, after all the
// existing tabs open on the page have been closed, since previously cached
// resources are updated in the background.

// To learn more about the benefits of this model and instructions on how to
// opt-in, read https://cra.link/PWA

type Config = {
	onSuccess?: (registration: ServiceWorkerRegistration) => void;
	onUpdate?: (registration: ServiceWorkerRegistration) => void;
};

const isLocalhost = Boolean(
	window.location.hostname === 'localhost' ||
		window.location.hostname === '[::1]' ||
		/^127(?:\.(?:25[0-5]|2[0-4]\d|[01]?\d\d?)){3}$/.exec(window.location.hostname),
);

const invokeServiceWorkerUpdateFlow = (registration: ServiceWorkerRegistration) => {
	if (confirm(`New app update is available!. Click OK to refresh to ${VERSION_NO} version`) && registration.waiting) {
		// let waiting Service Worker know it should became active
		registration.waiting.postMessage('SKIP_WAITING');
		window.location.reload();
	}
};

const registerValidSW = async (swUrl: string, config?: Config) => {
	try {
		await navigator.serviceWorker.register(swUrl).then(async (registration) => {
			// Check for updates at start.
			await registration.update();
			// Check for updates every min.
			setInterval(() => {
				void registration.update();
			}, 1000 * 60);

			// ensure the case when the updatefound event was missed is also handled
			// by re-invoking the prompt when there's a waiting Service Worker
			if (registration.waiting) {
				invokeServiceWorkerUpdateFlow(registration);
			}
			registration.onupdatefound = () => {
				const installingWorker = registration.installing;
				if (installingWorker == null) {
					return;
				}
				installingWorker.onstatechange = () => {
					if (installingWorker.state === 'installed') {
						invokeServiceWorkerUpdateFlow(registration);
						config?.onUpdate?.(registration);
					} else {
						console.log('Content is cached for offline use.');
						config?.onSuccess?.(registration);
					}
				};
			};
		});
	} catch (error) {
		console.error('Error during service worker registration :', error);
	}
};

const checkValidServiceWorker = async (swUrl: string, config?: Config) => {
	try {
		const response = await fetch(swUrl, {
			headers: { 'Service-Worker': 'script' },
		});

		const contentType = response.headers.get('content-type');
		if (response.status === 404 || (contentType != null && contentType.indexOf('javascript') === -1)) {
			const registration = await navigator.serviceWorker.ready;
			await registration.unregister();
			window.location.reload();
		} else {
			await registerValidSW(swUrl, config);
		}
	} catch (error) {
		console.log('No internet connection found. App is running in offline mode.');
	}
};

const register = (config?: Config): void => {
	if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
		window.addEventListener('load', () => {
			const wBranchName = _.get(window, 'branchname', '');
			const branchname = wBranchName ? `${wBranchName}/` : '';
			const swUrl = `${window.location.origin}/${branchname}sw.js`;

			if (isLocalhost) {
				checkValidServiceWorker(swUrl, config)
					.then(() => {
						void navigator.serviceWorker.ready.then(() => {
							console.log(
								'This web app is being served cache-first by a service ' +
									'worker. To learn more, visit https://cra.link/PWA',
							);
						});
					})
					.catch((error) => {
						console.error('Error during service worker registration:', error);
					});
			} else {
				registerValidSW(swUrl, config)
					.then(() => {
						console.log('Service worker registered successfully.');
					})
					.catch((error) => {
						console.error('Error during service worker registration:', error);
					});
			}
		});
	}
};

const unregister = async (): Promise<void> => {
	if ('serviceWorker' in navigator) {
		try {
			const registration = await navigator.serviceWorker.ready;
			await registration.unregister();
		} catch (error) {
			if (error instanceof Error) {
				console.error(error.message);
			} else {
				console.error('An unknown error occurred', error);
			}
		}
	}
};

export { register, unregister };
